<script setup lang="ts">
import { computed, ref, watch, type PropType, type Ref } from 'vue'
import { deletePostById, type LifeFileDTO, type MemorialPostDTO } from '@/composables/memorize/memorize'
import type { CommonResponse } from '@/apis/api'
import { CODE_OK } from '@/apis/code'
import { ElMessage, ElMessageBox } from 'element-plus'
import { handleErr } from '@/composables/exception/handler'
import { renderMessage } from '@/utils/util'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  post: {
    type: Object as PropType<MemorialPostDTO>,
    rquired: true
  },
  anim: {
    type: Boolean,
    default: false,
    required: false
  },
  canEdit: {
    type: Boolean,
    default: true,
    required: false
  }
})

const post = computed(() => props.post)

const canEdit = computed(() => props.canEdit)

const reverse = ref(false)
const moreVisible = ref(false)
let moreRef = ref(null)

const emit = defineEmits(['onMediaClick', 'onDelete', 'animationEnd'])

function judgeDelete() {
  moreVisible.value = false
  ElMessageBox.confirm(post?.value.postType === 'story' ? t('m.confirm_delete_memory') : t('m.confirm_delete_album'), {
    confirmButtonText: t('action.confirm'),
    cancelButtonText: t('action.cancel'),
    type: 'warning'
  })
    .then(() => {
      deletePost()
    })
    .catch(() => {})
}

async function deletePost() {
  const { data, error, statusCode } = await deletePostById(post.value?.id!)
  if (error.value) {
    handleErr(data, error, statusCode)
  } else {
    const resData = data as unknown as Ref<CommonResponse<any>>
    if (resData.value.code === CODE_OK) {
      // success
      emit('onDelete', post.value?.id)
      ElMessage({
        message: renderMessage(t('common.delete_success')),
        type: 'success'
      })
    } else {
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'warning'
      })
    }
  }
}

const animPlayed = ref(false)
watch(
  () => props.anim,
  (val) => {
    animPlayed.value = val
    setTimeout(() => {
      emit('animationEnd')
      console.log('animationEnd')
    }, 1500)
  }
)

function onMediaClick(file: LifeFileDTO) {
  emit('onMediaClick', post.value, file)
}
</script>
<template>
  <div :class="animPlayed ? 'animated-background' : ''" flex="~ col" w-full rounded-3 bg-theme-light p-4>
    <div flex :class="post?.postType === 'story' ? 'flex-col' : 'flex-col-reverse'" px-2>
      <div w-full>
        <p pt-1 leading-9 break-words text-4.5 text-theme-dark>
          {{ post?.content }}
        </p>
      </div>
      <ImageGrid
        v-if="post && post?.files && post?.files?.length > 0"
        :files="post?.files"
        @onMediaClick="onMediaClick"
        :class="post?.postType === 'story' ? 'mt-6 mb-3' : 'my-2'" />
      <div flex="~ row wrap" v-if="post?.tags && post?.tags.length > 0" mt-4>
        <RTag v-for="(item, index) in post.tags" :key="index" :title="item.name" mr-2 :simple="true"></RTag>
      </div>
    </div>
    <div flex="~ row" w-full mt-4 mb-1 px-2 items-center>
      <SignatureItem
        grow
        :avatar="post?.creator?.avatar"
        :wechatInfo="post?.creator?.wechatUserInfo"
        :fullName="post?.creator?.name?.fullName || ''"
        :relationName="post?.creatorRelation?.name || ''"
        :displayTime="post?.createdAt" />
      <div v-if="canEdit" ref="moreRef" mb-1>
        <RIcon
          icon="i-iconamoon:menu-kebab-horizontal-fill"
          :reverse="reverse"
          size="medium"
          @click.prevent.stop="moreVisible = !moreVisible"></RIcon>
        <RToolTip :wrapper-ref="moreRef" placement="left" :arrow="false" v-model="moreVisible">
          <div flex flex-row items-center text-theme-dark>
            <div flex="~ row" items-center quick-menu-item @click="judgeDelete" text-danger>
              <i i-mi:delete></i>
              <p px-2>{{ $t('action.delete') }}</p>
            </div>
          </div>
        </RToolTip>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped></style>
